import React from 'react';
import { useUserSettings } from '@/hooks/useUserSettings';
import { Database, Sparkles, Loader2 } from 'lucide-react';

export function ProfileUsage() {
  const { settings, isLoading } = useUserSettings();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!settings) {
    return null;
  }

  const storagePercent = (settings.storage_used_mb / settings.storage_limit_mb) * 100;
  const aiPercent = (settings.ai_requests_used / settings.ai_requests_limit) * 100;

  const getBarColor = (percent: number, isAI = false) => {
    if (percent > 90) return 'bg-destructive';
    if (percent > 70) return 'bg-amber-500';
    return isAI ? 'bg-partnr' : 'bg-emerald-500';
  };

  return (
    <div className="px-5 py-4 space-y-4">
      {/* Storage */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Database className="h-3.5 w-3.5 text-emerald-600" />
            </div>
            <span className="text-sm font-medium text-foreground">Storage</span>
          </div>
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{settings.storage_used_mb.toFixed(1)}</span>
            <span className="text-xs"> / {settings.storage_limit_mb}MB</span>
          </span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${getBarColor(storagePercent)}`}
            style={{ width: `${Math.min(storagePercent, 100)}%` }}
          />
        </div>
      </div>
      
      {/* AI Requests */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-partnr/10 flex items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-partnr" />
            </div>
            <span className="text-sm font-medium text-foreground">Partnr AI</span>
          </div>
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{settings.ai_requests_used}</span>
            <span className="text-xs"> / {settings.ai_requests_limit}</span>
          </span>
        </div>
        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${getBarColor(aiPercent, true)}`}
            style={{ width: `${Math.min(aiPercent, 100)}%` }}
          />
        </div>
      </div>
    </div>
  );
}
